/**
 * Zod Schemas for Access Control Types
 *
 * Provides runtime validation schemas for DocRole, DocACLEntry, DocACL, and ShareRequest.
 * These schemas enable validation of ACL data at API boundaries.
 *
 * @module schemas/acl.zod
 * @see SPEC-015 Requirements 1.4, 1.6, 3.1
 */
import { z } from 'zod';
import { DocRole } from '../types/acl';
import { DocIdSchema } from './doc.zod';
/**
 * Schema for DocRole enum using z.nativeEnum().
 * Validates that the value is one of OWNER, EDITOR, or VIEWER.
 */
export const DocRoleSchema = z.nativeEnum(DocRole);
/**
 * Schema for DocACLEntry - individual ACL entry.
 * Validates userId, role, and optional metadata fields.
 */
export const DocACLEntrySchema = z.object({
    userId: z.string().min(1, 'userId is required'),
    role: DocRoleSchema,
    grantedAtMs: z.number().optional(),
    grantedBy: z.string().min(1).optional(),
});
/**
 * Schema for DocACL - document access control list with Record entries.
 * Uses Record<string, DocRole> for O(1) lookup.
 */
export const DocACLSchema = z.object({
    docId: DocIdSchema,
    entries: z.record(z.string(), DocRoleSchema),
});
/**
 * Legacy schema for DocACL with array entries.
 * @deprecated Use DocACLSchema with Record entries instead
 */
export const DocACLLegacySchema = z.object({
    docId: DocIdSchema,
    entries: z.array(DocACLEntrySchema),
});
/**
 * Schema for ShareRequest - request to share a document with a user.
 * Validates userId and role fields.
 *
 * @see Requirement 3.1
 */
export const ShareRequestSchema = z.object({
    userId: z.string().min(1, 'userId is required'),
    role: DocRoleSchema,
});
/**
 * Schema for ShareListResponse - response listing document shares.
 *
 * @see Requirement 3.3
 */
export const ShareListResponseSchema = z.object({
    docId: z.string().min(1),
    shares: z.array(DocACLEntrySchema),
});
//# sourceMappingURL=acl.zod.js.map